import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const rarityColor = {
  "Common": "text-zinc-400 border-zinc-700",
  "Uncommon": "text-emerald-400 border-emerald-500/40",
  "Rare": "text-sky-400 border-sky-500/40",
  "Ultra Rare": "text-violet-400 border-violet-500/50",
  "Secret Rare": "text-fuchsia-400 border-fuchsia-500/50",
  "Sealed": "text-amber-400 border-amber-500/40",
};

export const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 8) * 0.05 }}
      data-testid={`product-card-${product.id}`}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-card"
    >
      <Link to={`/product/${product.id}`}>
        <div className="relative aspect-[4/5] overflow-hidden bg-zinc-900">
          <img src={product.image} alt={product.name} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="foil-shine pointer-events-none absolute inset-0" />
          <div className="absolute left-3 top-3">
            <span className={`overline rounded-full border bg-black/50 px-2.5 py-1 backdrop-blur ${rarityColor[product.rarity] || rarityColor.Common}`}>
              {product.rarity}
            </span>
          </div>
          {product.grade ? (
            <div className="absolute right-3 top-3" data-testid={`grade-badge-${product.id}`}>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-black/60 px-2.5 py-1 text-[11px] font-800 uppercase tracking-wide text-amber-300 backdrop-blur">
                <ShieldCheck size={12} /> {product.grade}
              </span>
            </div>
          ) : null}
        </div>
      </Link>
      <div className="p-4">
        <Link to={`/product/${product.id}`}>
          <h3 className="line-clamp-1 font-display text-base font-700 text-white transition-colors group-hover:text-secondary">{product.name}</h3>
        </Link>
        <p className="mt-1 text-xs text-zinc-500">{product.category}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="font-display text-lg font-800 text-white">${product.price.toFixed(2)}</span>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={() => { add(product); toast.success(`${product.name} added to cart`); }}
            disabled={product.stock <= 0}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
          >
            <Plus size={15} /> Add
          </button>
        </div>
      </div>
    </motion.div>
  );
};
